import React, { useContext, useState } from "react";
import {
  GridContainer,
  BillingInfoListRootArea,
  GridItem,
} from "./BillingInfo.styles";
import { Box, Button, TextField, Typography } from "@mui/material";
import DataTable from "../../dataTable/DataTable";
import { useEffectOnce } from "../../../utils/effects";
import { useDispatch, useSelector } from "react-redux";
import { deleteBillingInfo, getBillingInfoList } from "./BillingInfo.thunk";
import {
  setBillingInfoId,
  ResetStore,
} from "../../forms/BillingInfo/BillingInfoForm.slice";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { setClientInfoId } from "../Invoices/Invoices.slice";
import AuthContext from "../../../context/AuthContext";
import { useConfirmModal } from "../../confirmModal/useConfirmModal";

const BillingInfo = (props) => {
  const { setSubmenuMerchantTerminal, setSelectedOption, setNameSection } =
    props;

  const dispatch = useDispatch();
  const { showConfirmModal } = useConfirmModal();

  const { billingInfoList, status, page, pageSize, totalRecords } =
    useSelector((state) => state.billingInfo);

  const { token, userInfo } = useContext(AuthContext);
  const merchantID = userInfo["acquirer-id-merchant-data"] || undefined;

  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const buildListPayload = (overrides = {}) => {
    const payload = {
      token,
      page: overrides.page ?? page,
      page_size: overrides.page_size ?? pageSize,
      merchantID,
    };
    const search = "search" in overrides ? overrides.search : appliedSearch;
    if (search) {
      payload.search = search;
    }
    return payload;
  };

  const loadList = (overrides = {}) => {
    dispatch(getBillingInfoList(buildListPayload(overrides)));
  };

  useEffectOnce(() => {
    loadList();
  });

  const handlePageChange = (event, newPage) => {
    loadList({ page: newPage + 1 });
  };

  const handleRowsPerPageChange = (event) => {
    const newPageSize = +event.target.value;
    loadList({ page: 1, page_size: newPageSize });
  };

  const handleSearch = () => {
    const nextSearch = searchQuery.trim();
    setAppliedSearch(nextSearch);
    loadList({ page: 1, search: nextSearch || undefined });
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setAppliedSearch("");
    loadList({ page: 1, search: "" });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleAdd = () => {
    dispatch(ResetStore());
    dispatch(setBillingInfoId(null));
    setSubmenuMerchantTerminal("billing_info_form");
    setSelectedOption("billing_info_form");
    setNameSection("Merchant & Terminal - Create Customer Info");
  };

  const handleEdit = (row) => {
    dispatch(ResetStore());
    dispatch(setBillingInfoId(row.id));
    setSubmenuMerchantTerminal("billing_info_form");
    setSelectedOption("billing_info_form");
    setNameSection("Merchant & Terminal - Edit Customer Info");
  };

  const handleViewInvoices = (row) => {
    dispatch(setClientInfoId(row.id));
    setSubmenuMerchantTerminal("invoices");
    setSelectedOption("invoices");
    setNameSection("Merchant & Terminal - Invoices");
  };

  const handleDelete = (row) => {
    const label =
      row.company_name ||
      `${row.first_name || ""} ${row.last_name || ""}`.trim() ||
      row.email ||
      `Billing Info #${row.id}`;

    showConfirmModal({
      message: `Are you sure you want to archive billing info for ${label}? This action cannot be undone.`,
      actionId: `delete-billing-info-${row.id}`,
      title: "Archive Billing Info",
      onConfirm: () => {
        dispatch(
          deleteBillingInfo({
            token,
            billingInfoId: row.id,
            page,
            page_size: pageSize,
            merchantID,
            search: appliedSearch || undefined,
          })
        );
      },
    });
  };

  const columns = [
    { id: "email", label: "Email", minWidth: 200 },
    { id: "company_name", label: "Company Name", minWidth: 150 },
    {
      id: "internal_company_number",
      label: "Internal #",
      minWidth: 120,
      format: (value, row) => {
        const raw =
          value
          ?? row?.internal_company_number
          ?? row?.internalCompanyNumber;
        const trimmed = typeof raw === "string" ? raw.trim() : "";
        return trimmed || "—";
      },
    },
    {
      id: "name",
      label: "Name",
      minWidth: 200,
      format: (value, row) => {
        if (!row) return value;
        const firstName = row.first_name || "";
        const lastName = row.last_name || "";
        return `${firstName} ${lastName}`.trim() || "-";
      },
    },
    { id: "city", label: "City", minWidth: 120 },
    {
      id: "action",
      label: "Actions",
      actions: [
        {
          icon: <AccountBalanceIcon />,
          label: "View Invoices",
          onClick: (row) => handleViewInvoices(row),
        },
        {
          icon: <EditIcon />,
          label: "Edit Billing Info",
          onClick: (row) => handleEdit(row),
        },
        {
          icon: <DeleteIcon />,
          label: "Archive Billing Info",
          onClick: (row) => handleDelete(row),
        },
      ],
    },
  ];

  return (
    <BillingInfoListRootArea>
      <GridContainer>
        <GridItem xs={12}>
          <Box
            display="flex"
            flexWrap="wrap"
            gap={1.5}
            alignItems="center"
            sx={{ mt: 2, mb: 1.5 }}
          >
            <TextField
              label="Search"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Email, name, company, city, or internal #"
              sx={{ minWidth: { xs: "100%", sm: 240 }, flexGrow: 1 }}
            />
            <Button variant="contained" size="small" onClick={handleSearch}>
              Search
            </Button>
            <Button variant="text" size="small" onClick={handleClearSearch}>
              Clear
            </Button>
          </Box>
        </GridItem>

        <GridItem xs={12}>
          <Box
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">Customer Info</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAdd}
              size="small"
            >
              Add
            </Button>
          </Box>
        </GridItem>

        <GridItem xs={12}>
          <DataTable
            loading={status === "loading"}
            columns={columns}
            rows={billingInfoList}
            page={page - 1}
            rowsPerPage={pageSize}
            count={totalRecords}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            noDataText="No billing information available"
          />
        </GridItem>
      </GridContainer>
    </BillingInfoListRootArea>
  );
};

export default BillingInfo;
